import * as React from 'react';
import { DropdownPopover, Outside } from '@cian/ui-kit';

import { IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';

import * as styles from './FiltersDropdownPopover.css';
import { useFocusedItem } from './hooks';
import { IDialogFilterItem } from '../../../../types/filterSelectors';

interface IFiltersDropdownPopoverProps {
  button: React.ReactNode;
  buttonRef: React.RefObject<HTMLLabelElement>;
  filters: IDialogFilterItem[];
  open: boolean;
  onClose: () => void;
  onFilter: (item: IDialogFilterItem) => void;
}

export const FiltersDropdownPopover: React.FC<IFiltersDropdownPopoverProps> = ({
  button,
  buttonRef,
  filters,
  open,
  onClose,
  onFilter,
}) => {
  const insideRefs = React.useMemo(() => [buttonRef], [buttonRef]);

  const { focusedIndex, setFocusedIndex, focusedItemRef } = useFocusedItem({ isPopoverOpen: open });

  const handleListKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.code) {
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          onClose();

          if (buttonRef.current) {
            buttonRef.current.focus();
          }
          break;
        case 'Enter':
        case 'Space':
          event.preventDefault();
          onFilter(filters[focusedIndex]);

          if (buttonRef.current) {
            buttonRef.current.focus();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(Math.max(focusedIndex - 1, 0));
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(Math.min(focusedIndex + 1, filters.length - 1));
          break;
      }
    },
    [buttonRef, filters, focusedIndex, onClose, onFilter, setFocusedIndex],
  );

  return (
    <DropdownPopover
      open={open}
      content={
        <Outside insideRefs={insideRefs} onOutside={onClose}>
          <div className={styles['popover-content']}>
            <ul
              className={styles['popover-list']}
              role="listbox"
              tabIndex={-1}
              data-testid="ChipDialogList"
              onKeyDown={handleListKeyDown}
            >
              {filters.map((item, index) => (
                <li
                  key={index}
                  className={styles['popover-list-item']}
                  role="option"
                  aria-checked={item.isActive}
                  ref={index === focusedIndex ? focusedItemRef : null}
                  onClick={() => onFilter(item)}
                  tabIndex={0}
                >
                  {item.name}
                  {item.isActive && <IconFunctionalSuccess16 color="icon-main-default" />}
                </li>
              ))}
            </ul>
          </div>
        </Outside>
      }
    >
      <div>{button}</div>
    </DropdownPopover>
  );
};
